import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Package, History, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsumptionInventory = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockOutHistory, setStockOutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  // Dialog states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editItem, setEditItem] = useState(null);

  // Form
  const [form, setForm] = useState({
    in_date: new Date().toISOString().split("T")[0], category: "", item_name: "", unit: "kg", total_qty: "", stock_count: "", unit_price: "", pantone_color: "", notes: "",
  });
  const [newCategoryName, setNewCategoryName] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/consumption-inventory`),
        axios.get(`${API}/consumption-categories`),
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data);
    } catch (err) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/consumption-stock-out/history`);
      setStockOutHistory(res.data);
    } catch (err) { toast.error("Failed to load history"); }
  };

  const handleAdd = async () => {
    if (!form.category || !form.item_name || !form.total_qty) {
      toast.error("Category, name, and quantity are required"); return;
    }
    try {
      await axios.post(`${API}/consumption-inventory`, { ...form, available_qty: form.total_qty });
      toast.success("Item added");
      setIsAddOpen(false);
      resetForm();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const handleEdit = async () => {
    if (!editItem) return;
    try {
      await axios.put(`${API}/consumption-inventory/${editItem.id}`, form);
      toast.success("Item updated");
      setIsEditOpen(false);
      resetForm();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/consumption-inventory/${deleteTarget.id}`);
      toast.success("Item deleted");
      setDeleteTarget(null);
      fetchAll();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/consumption-categories`, { name: newCategoryName.trim() });
      toast.success("Category added");
      setNewCategoryName("");
      setIsAddCategoryOpen(false);
      fetchAll();
    } catch (err) { toast.error("Failed to add category"); }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      await axios.delete(`${API}/consumption-categories/${catId}`);
      toast.success("Category removed");
      fetchAll();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      in_date: item.in_date || (item.created_at ? item.created_at.split("T")[0] : ""),
      category: item.category, item_name: item.item_name, unit: item.unit,
      total_qty: item.total_qty, stock_count: item.stock_count || "",
      unit_price: item.unit_price || "",
      pantone_color: item.pantone_color || "", notes: item.notes || "",
      available_qty: item.available_qty, available_count: item.available_count || "",
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setForm({ in_date: new Date().toISOString().split("T")[0], category: "", item_name: "", unit: "kg", total_qty: "", stock_count: "", unit_price: "", pantone_color: "", notes: "" });
    setEditItem(null);
  };

  const filtered = items.filter(i => {
    const matchSearch = !searchTerm || i.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) || i.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filterCategory === "all" || i.category === filterCategory;
    return matchSearch && matchCat;
  });

  const uniqueCategories = [...new Set(items.map(i => i.category))];

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-8 w-8 animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-emerald-400 tracking-wider" data-testid="consumption-inventory-title">
            Consumption Inventory
          </h1>
          <p className="text-gray-400 text-sm mt-1">{items.length} items across {uniqueCategories.length} categories</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => { resetForm(); setIsAddOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-consumption-item-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Item
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsAddCategoryOpen(true)} className="border-emerald-500/30 text-emerald-400" data-testid="manage-categories-btn">
            <Plus className="h-4 w-4 mr-1" /> Category
          </Button>
          <Button size="sm" variant="outline" onClick={() => { fetchHistory(); setIsHistoryOpen(true); }} className="border-gray-600 text-gray-300" data-testid="stock-out-history-btn">
            <History className="h-4 w-4 mr-1" /> Stock Out Log
          </Button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search items..." className="pl-9 bg-gray-800 border-gray-700 text-white" data-testid="consumption-search" />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="h-10 px-3 rounded-md bg-gray-800 border border-gray-700 text-white text-sm" data-testid="consumption-category-filter">
          <option value="all">All Categories</option>
          {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-emerald-400 font-bold">In Date</TableHead>
                  <TableHead className="text-emerald-400 font-bold">Category</TableHead>
                  <TableHead className="text-emerald-400 font-bold">Item Name</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Total</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Available</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Used</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Unit</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Nos</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Price/Unit</TableHead>
                  <TableHead className="text-emerald-400 font-bold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-500">No items found. Add your first consumption item.</TableCell></TableRow>
                ) : filtered.map(item => (
                  <TableRow key={item.id} className="border-gray-800 hover:bg-gray-800/50" data-testid={`consumption-row-${item.id}`}>
                    <TableCell className="text-gray-400 text-xs whitespace-nowrap">{item.in_date ? new Date(item.in_date).toLocaleDateString("en-IN") : new Date(item.created_at).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="text-gray-300 text-sm">{item.category}</TableCell>
                    <TableCell className="text-white font-medium">
                      {item.item_name}
                      {item.pantone_color && <span className="ml-1 text-purple-300 text-xs">({item.pantone_color})</span>}
                    </TableCell>
                    <TableCell className="text-center text-gray-400">{item.total_qty}</TableCell>
                    <TableCell className="text-center">
                      <span className={`font-bold ${item.available_qty <= 0 ? 'text-red-400' : item.available_qty < item.total_qty * 0.2 ? 'text-yellow-400' : 'text-emerald-400'}`}>
                        {item.available_qty}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-orange-400">{item.used_qty || 0}</TableCell>
                    <TableCell className="text-center text-gray-400">{item.unit}</TableCell>
                    <TableCell className="text-center">
                      {item.stock_count > 0 ? (
                        <span className={`font-bold ${(item.available_count || 0) <= 0 ? 'text-red-400' : 'text-cyan-400'}`}>
                          {item.available_count || 0}/{item.stock_count}
                        </span>
                      ) : <span className="text-gray-600">—</span>}
                    </TableCell>
                    <TableCell className="text-center text-gray-400">{item.unit_price ? `₹${item.unit_price}` : "—"}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="h-7 w-7 p-0 text-gray-400 hover:text-cyan-400" data-testid={`edit-consumption-${item.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(item)} className="h-7 w-7 p-0 text-gray-400 hover:text-red-400" data-testid={`delete-consumption-${item.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isAddOpen || isEditOpen} onOpenChange={(open) => { if (!open) { setIsAddOpen(false); setIsEditOpen(false); resetForm(); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">{isEditOpen ? "Edit Item" : "Add Consumption Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">In Date</Label>
                <Input type="date" value={form.in_date} onChange={e => setForm(p => ({ ...p, in_date: e.target.value }))} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-in-date" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Category</Label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-700 text-white text-sm mt-1" data-testid="form-category">
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Item Name</Label>
              <Input value={form.item_name} onChange={e => setForm(p => ({ ...p, item_name: e.target.value }))} placeholder="e.g., Corn Starch 25kg bag" className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-item-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Quantity</Label>
                <Input type="number" value={form.total_qty} onChange={e => setForm(p => ({ ...p, total_qty: e.target.value }))} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-qty" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Unit</Label>
                <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-700 text-white text-sm mt-1" data-testid="form-unit">
                  <option value="kg">kg</option>
                  <option value="litre">litre</option>
                  <option value="pcs">pcs</option>
                  <option value="drum">drum</option>
                  <option value="roll">roll</option>
                  <option value="meter">meter</option>
                  <option value="box">box</option>
                  <option value="bundle">bundle</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">No. of Units</Label>
                <Input type="number" value={form.stock_count} onChange={e => setForm(p => ({ ...p, stock_count: e.target.value }))} placeholder="e.g., 4 drums" className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-stock-count" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Price/Unit (₹)</Label>
                <Input type="number" value={form.unit_price} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-price" />
              </div>
            </div>
            {form.category?.includes("Ink") && (
              <div>
                <Label className="text-gray-400 text-xs">Pantone Color</Label>
                <Input value={form.pantone_color} onChange={e => setForm(p => ({ ...p, pantone_color: e.target.value }))} placeholder="e.g., PMS 186 C" className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-pantone" />
              </div>
            )}
            <div>
              <Label className="text-gray-400 text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddOpen(false); setIsEditOpen(false); resetForm(); }} className="border-gray-700 text-gray-400">Cancel</Button>
            <Button onClick={isEditOpen ? handleEdit : handleAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="form-submit-btn">
              {isEditOpen ? "Update" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Remove <span className="text-white font-medium">{deleteTarget?.item_name}</span> from consumption inventory?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-700 text-gray-400">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Categories Dialog */}
      <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">Manage Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="New category name" className="bg-gray-800 border-gray-700 text-white" data-testid="new-category-input" />
              <Button onClick={handleAddCategory} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="add-category-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {categories.map(c => (
                <div key={c.id} className="flex justify-between items-center px-3 py-2 bg-gray-800 rounded-md">
                  <span className="text-sm text-gray-200">{c.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(c.id)} className="h-6 w-6 p-0 text-gray-500 hover:text-red-400">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Out History Dialog */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">Stock Out History</DialogTitle>
          </DialogHeader>
          {stockOutHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No stock out records yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800">
                  <TableHead className="text-emerald-400">Date</TableHead>
                  <TableHead className="text-emerald-400">Item</TableHead>
                  <TableHead className="text-emerald-400">Category</TableHead>
                  <TableHead className="text-emerald-400 text-center">Qty</TableHead>
                  <TableHead className="text-emerald-400">Taken By</TableHead>
                  <TableHead className="text-emerald-400">Purpose</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockOutHistory.map(log => (
                  <TableRow key={log.id} className="border-gray-800">
                    <TableCell className="text-gray-400 text-xs">{new Date(log.timestamp).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-white text-sm">{log.item_name}</TableCell>
                    <TableCell className="text-gray-400 text-sm">{log.category}</TableCell>
                    <TableCell className="text-center text-orange-400 font-medium">{log.quantity} {log.unit}</TableCell>
                    <TableCell className="text-gray-300 text-sm">{log.person_name}</TableCell>
                    <TableCell className="text-gray-400 text-sm">{log.purpose || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsumptionInventory;
